// Saved locations + address-search hooks.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { locationsApi, qk } from "../api";
import type { LocationInput } from "../api";

export function useLocations() {
  return useQuery({
    queryKey: qk.locations.root,
    queryFn: ({ signal }) => locationsApi.list(signal),
  });
}

/** Debounced address autocomplete. Disabled for blank/very short queries. */
export function useLocationSearch(q: string) {
  return useQuery({
    queryKey: qk.locations.search(q),
    queryFn: ({ signal }) => locationsApi.search(q, signal),
    enabled: q.trim().length >= 3,
    staleTime: 5 * 60_000,
  });
}

export function useCreateLocation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: LocationInput) => locationsApi.create(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.locations.root });
    },
  });
}

export function useUpdateLocation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: LocationInput }) =>
      locationsApi.update(id, body),
    onSuccess: (_l, { id }) => {
      void qc.invalidateQueries({ queryKey: qk.locations.detail(id) });
      void qc.invalidateQueries({ queryKey: qk.locations.root });
    },
  });
}

export function useDeleteLocation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => locationsApi.remove(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.locations.root });
    },
  });
}
